import { ReactNode, useEffect, useMemo } from "react";
import { INFORMATION_TABS, informationTabs, InformationTabsValue, PATH_PARAMS } from "./information-types";
import { Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { CompanyInformation, InvoiceHistory, UserManagement } from "./components";
import { informationPageK, useInformationPageT } from "src/translation-hooks/information-page";
import { useGetCustomerDetails, usePermissionCheck } from "src/hooks";
import { Shared_ContactPersonPermissionKinds } from "src/backend/openapi/v1";
import { CreditNote } from "./components/invoice-history/credit-note";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES_URL } from "src/utils/constants";

export const Information = () => {
  const informationPageT = useInformationPageT();
  const theme = useTheme();
  const navigate = useNavigate();

  const { customerDetails } = useGetCustomerDetails();
  const { currentTab } = useParams<PATH_PARAMS>();

  const [isAllowedToViewInvoice] = usePermissionCheck([Shared_ContactPersonPermissionKinds.ViewInvoice]);

  useEffect(() => {
    if (!currentTab) navigate(ROUTES_URL.INFORMATION.build(informationTabs[0]));
  }, [currentTab]);

  const currentTabComponent = useMemo((): { component: ReactNode; resetChanges?: () => void; onSave?: () => void } => {
    switch (currentTab) {
      case INFORMATION_TABS.COMPANY_INFORMATION:
        return { component: <CompanyInformation /> };
      case INFORMATION_TABS.USERS:
        return { component: <UserManagement /> };
      case INFORMATION_TABS.INVOICE_HISTORY:
        return { component: <InvoiceHistory /> };
      case INFORMATION_TABS.CREDIT_NOTE:
        return { component: <CreditNote /> };

      default:
        return { component: <></> };
    }
  }, [currentTab]);

  const handleTabChange = (newTab: InformationTabsValue) => {
    navigate(ROUTES_URL.INFORMATION.build(newTab));
  };

  return (
    <Stack flex={1} overflow="auto" sx={{ background: theme.palette.customColors.lightGrey }}>
      <Stack marginBottom={2}>
        <Typography variant="h2" color={theme.palette.customColors.darkGrey}>
          {customerDetails?.name}
        </Typography>
      </Stack>
      <Stack>
        <Tabs
          value={currentTab}
          onChange={(_, value) => {
            handleTabChange(value);
          }}
        >
          {informationTabs
            .filter(tab => !(tab === "invoiceHistory" && !isAllowedToViewInvoice))
            .map(tab => {
              return <Tab key={tab} data-itk-test={informationPageK[tab]} value={tab} label={informationPageT[tab]} />;
            })}
        </Tabs>
      </Stack>
      <Stack flex={1} overflow="auto" marginTop={1}>
        {currentTabComponent.component}
      </Stack>
    </Stack>
  );
};
